import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { zeosSuccess } from "../../../components/Shared/confirm-dialog/ConfirmDialog";
import { ZeosButton } from "../../../components/Shared/ZeosButton";
import { ZeosInput } from "../../../components/Shared/ZeosInput";
import { ZeosModal } from "../../../components/Shared/ZeosModal";
import { FeatureFlags } from "../../../configs/FeatureFlags";
import { ZeosApiContext } from "../../../context/ZeosApiContext";
import { handleZeosEnviromentEmailWhitelist } from "../../../services/environment/EnvironmentHelpers";
import { createLogger } from "../../../services/Logger";
import { PropertyForm } from "../../../forms/PropertyForm";
import { useFormValidation } from "../../../hooks/useFormValidation";
import { defaultPropertyForm } from "../../../services/forms/DefaultFormData";
import { IPropertyFormDataType } from "../../../services/forms/FormDataTypes";
import { PropertyValidationSchema } from "../../../services/forms/validationSchemas/PropertyValidationSchema";
import {
  mapPropertyContractToFormData,
  mapPropertyFormToContract,
} from "../../../services/helpers/PropertyHelpers";
import { IListItemSimple } from "../../../services/zeos-api/contracts/ListUtility";
import { ZeosListEnum, ZeosListOptionEnum } from "../../../services/zeos-api/enums/ListUtility";
import {
  IAdminCustomerResponse,
  IAdminCustomerSettingItem,
  IAdminPropertyResponse,
  IAdminPropertySettingItem,
  IAdminUserCreateRequest,
  IAdminUserResponse,
} from "../../../services/zeos-api/contracts/Admin";
import {
  IAdminIncentiveProgramRequest,
  IAdminIncentiveProgramResponse,
  IIncentiveAdminDiagnosticsResponse,
  IIncentiveReferenceResponse,
  IIncentiveResearchRequest,
  IIncentiveResearchResult,
} from "../../../services/zeos-api/contracts/IncentiveAdmin";
import { IncentiveEligibilityVectorFields } from "./IncentiveEligibilityVectorFields";
import { AttachUtilityAuthorization } from "./AttachUtilityAuthorization";
import { MeteringAttestationQueue } from "./MeteringAttestationQueue";
import { UnassignedMeterQueue } from "./UnassignedMeterQueue";
import {
  incentiveAreaAliases,
  incentiveCategoryAliases,
  incentiveTypeAliases,
  resolveReferenceId,
} from "./incentiveAdminMetadata";
import {
  buildIncentiveRequirementsSkeleton,
  mergeIncentiveRequirementsIdentity,
} from "./incentiveRequirementsVector";
import {
  findPropertySettingKey,
  IPropertySettingKeyDefinition,
  normalizeTriStateValue,
  parseBuildingUses,
  PROPERTY_SETTING_KEY_SUGGESTIONS,
  serializeBuildingUses,
  suggestPropertySettingKey,
  TRI_STATE_OPTIONS,
} from "./propertySettingKeys";

type TabKey =
  | "users"
  | "incentives"
  | "property-settings"
  | "customer-settings"
  | "screening-engine"
  | "testing-sandbox"
  | "metering-attestation";

type StatusState = {
  type: "success" | "error";
  message: string;
};

// Temporarily hide duplicate-on-edit; set to true to re-enable.
const ENABLE_INCENTIVE_PROGRAM_DUPLICATE = false;

// Must match PropertyService.ImmutableBaselinePropertyName in zeos-api -- the fixture name is
// the only thing marking it as the baseline, there's no DB flag.
const QA_BASELINE_PROPERTY_NAME = "QA-StoneMill";

// Common__CompanyRole 10 = "Admin" -- the role every user created from this screen gets.
const ADMIN_COMPANY_ROLE_ID = 10;

type SandboxPropertyRow = {
  propertyId: number;
  name: string;
  companyId: number;
  companyName: string;
  isBaseline: boolean;
  /**
   * Comes from the API's IsSandbox, which reads the lineage the clone flow
   * writes. This was inferred from the clone's name until the naming scheme
   * changed underneath it and clones started disappearing from this tab.
   */
  isSandbox: boolean;
  /** Reset restores a clone from its own source, so every clone can be reset. */
  canReset: boolean;
  /** Deleting a clone is safe; deleting a real building or the baseline is not. */
  canDelete: boolean;
};

const buildEmptyProgram = (): IAdminIncentiveProgramRequest => ({
  incentiveProgramId: null,
  name: "",
  programUrl: "",
  programStatus: "",
  description: "",
  contactName: "",
  contactEmail: "",
  contactPhone: "",
  incentiveSourceId: null,
  incentiveCategoryId: null,
  incentiveAreaId: null,
  incentiveTypeId: null,
  stateCode: "",
  isDacOnly: null,
  startDate: "",
  endDate: "",
  isPublished: false,
  incentiveId: null,
  incentiveName: "",
  incentiveDescription: "",
  maxIncentiveAmount: null,
  incentiveRate: null,
  incentiveRateUnit: "",
  costSharePercent: null,
  requirementsJson: buildIncentiveRequirementsSkeleton(),
});

const formatDateInput = (value?: string | null) => {
  if (!value) return "";
  return value.substring(0, 10);
};

// Mirrors Uri.TryCreate(..., UriKind.Absolute) plus the http/https scheme check
// in the API's ValidatePublishRequest, so "www.example.com" fails here too.
const isAbsoluteHttpUrl = (value?: string | null) => {
  const trimmed = value?.trim();
  if (!trimmed) return false;
  try {
    const parsed = new URL(trimmed);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
};

type IncentivePublishFilter = "all" | "published" | "draft" | "archived";

const formatAdminDate = (value?: string | null) => {
  if (!value) return "-";
  return new Date(value).toLocaleString();
};

export const AdminPanel = () => {
  const api = useContext(ZeosApiContext);
  const navigate = useNavigate();
  const log = useMemo(() => createLogger("AdminPanel"), []);

  const [activeTab, setActiveTab] = useState<TabKey>("users");
  const [status, setStatus] = useState<StatusState | null>(null);

  const [users, setUsers] = useState<IAdminUserResponse[]>([]);
  const [customers, setCustomers] = useState<IAdminCustomerResponse[]>([]);
  const [userLoading, setUserLoading] = useState(false);
  const [userForm, setUserForm] = useState<IAdminUserCreateRequest>({
    firstName: "",
    lastName: "",
    emailAddress: "",
    companyIds: [],
    propertyIds: [],
    isSuperAdmin: false,
  });

  const [programs, setPrograms] = useState<IAdminIncentiveProgramResponse[]>(
    [],
  );
  const [references, setReferences] =
    useState<IIncentiveReferenceResponse | null>(null);
  const [programForm, setProgramForm] =
    useState<IAdminIncentiveProgramRequest>(buildEmptyProgram());
  const [programLoading, setProgramLoading] = useState(false);
  const [includeDrafts, setIncludeDrafts] = useState(true);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [programSearch, setProgramSearch] = useState("");
  const [programSourceFilter, setProgramSourceFilter] = useState("");
  const [programCategoryFilter, setProgramCategoryFilter] = useState("");
  const [programStateFilter, setProgramStateFilter] = useState("");
  const [programPublishFilter, setProgramPublishFilter] =
    useState<IncentivePublishFilter>("all");
  const [programEditorOpen, setProgramEditorOpen] = useState(false);
  const [diagnostics, setDiagnostics] =
    useState<IIncentiveAdminDiagnosticsResponse | null>(null);
  const [diagnosticsLoading, setDiagnosticsLoading] = useState(false);

  const [researchForm, setResearchForm] = useState<IIncentiveResearchRequest>({
    area: "",
    geographyType: "",
    utilityTerritory: "",
    categoryFocus: "",
    notes: "",
    limit: 20,
  });
  const [researchLoading, setResearchLoading] = useState(false);
  const [researchResults, setResearchResults] = useState<
    IIncentiveResearchResult[]
  >([]);

  const [propertySettingsForm, setPropertySettingsForm] = useState<
    IAdminPropertySettingItem[]
  >([]);
  const [propertySettingsLoading, setPropertySettingsLoading] = useState(false);
  const [selectedPropertyId, setSelectedPropertyId] = useState<number | null>(
    null,
  );
  // Bumped after an authorization attach so the unassigned-meters list reloads.
  const [unassignedMetersKey, setUnassignedMetersKey] = useState(0);

  const [customerSettingsForm, setCustomerSettingsForm] = useState<
    IAdminCustomerSettingItem[]
  >([]);
  const [customerSettingsLoading, setCustomerSettingsLoading] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(
    null,
  );

  const [sandboxActionLoading, setSandboxActionLoading] = useState(false);

  // Edit Property (sandbox tiles) -- gives "Reset to Baseline" an actual use case: without a real,
  // persisting edit path, a cloned sandbox property could never drift from the baseline it was
  // cloned from (Screening Sandbox's own PropertyForm is deliberately ephemeral and never saves).
  const {
    formData: editPropertyFormData,
    setFormData: setEditPropertyFormData,
    errors: editPropertyErrors,
    formValidate: editPropertyFormValidate,
    formChange: editPropertyFormChange,
  } = useFormValidation<IPropertyFormDataType>(PropertyValidationSchema, defaultPropertyForm);
  const [sandboxCloneSourceId, setSandboxCloneSourceId] = useState<number | null>(
    null,
  );
  const [editPropertyModalOpen, setEditPropertyModalOpen] = useState(false);
  const [editPropertyCompanyId, setEditPropertyCompanyId] = useState<number | null>(null);
  const [editPropertyLoading, setEditPropertyLoading] = useState(false);
  const [editPropertySaving, setEditPropertySaving] = useState(false);
  const [editPropertyError, setEditPropertyError] = useState<string | null>(null);
  const [sandboxHvacOptions, setSandboxHvacOptions] = useState<IListItemSimple[]>([]);
  const [sandboxPriorWorkTypeOptions, setSandboxPriorWorkTypeOptions] = useState<
    { id: number; name: string }[]
  >([]);

  const referenceName = (
    list: { id: number; name: string }[] | undefined,
    id?: number | null,
  ) => {
    if (id === null || id === undefined) {
      return "-";
    }

    return list?.find((item) => item.id === id)?.name ?? `Unknown (${id})`;
  };

  const referenceOptions = (
    list: { id: number; name: string }[] | undefined,
    selectedId?: number | null,
  ) => {
    const options = list ?? [];
    if (
      selectedId === null ||
      selectedId === undefined ||
      options.some((item) => item.id === selectedId)
    ) {
      return options;
    }

    return [...options, { id: selectedId, name: `Unknown (${selectedId})` }];
  };

  const stateOptions = useMemo(() => {
    return Array.from(
      new Set(
        programs
          .map((program) => program.stateCode?.trim())
          .filter(Boolean) as string[],
      ),
    ).sort();
  }, [programs]);

  const filteredPrograms = useMemo(() => {
    const query = programSearch.trim().toLowerCase();
    return programs.filter((program) => {
      if (
        programSourceFilter &&
        program.incentiveSourceId !== Number(programSourceFilter)
      ) {
        return false;
      }
      if (
        programCategoryFilter &&
        program.incentiveCategoryId !== Number(programCategoryFilter)
      ) {
        return false;
      }
      if (programStateFilter && program.stateCode !== programStateFilter) {
        return false;
      }
      if (
        programPublishFilter === "published" &&
        (!program.isPublished || program.isDeleted)
      ) {
        return false;
      }
      if (
        programPublishFilter === "draft" &&
        (program.isPublished || program.isDeleted)
      ) {
        return false;
      }
      if (programPublishFilter === "archived" && !program.isDeleted) {
        return false;
      }
      if (!query) {
        return true;
      }

      const values = [
        program.incentiveProgramId?.toString(),
        program.incentiveId?.toString(),
        program.name,
        program.incentiveName,
        program.programStatus,
        program.stateCode,
        referenceName(references?.sources, program.incentiveSourceId),
        referenceName(references?.categories, program.incentiveCategoryId),
        referenceName(references?.areas, program.incentiveAreaId),
        referenceName(references?.types, program.incentiveTypeId),
      ];

      return values.some((value) => value?.toLowerCase().includes(query));
    });
  }, [
    programCategoryFilter,
    programPublishFilter,
    programSearch,
    programSourceFilter,
    programStateFilter,
    programs,
    references,
  ]);

  const missingPublishMetadata = (program: {
    incentiveCategoryId?: number | null;
    incentiveAreaId?: number | null;
    incentiveTypeId?: number | null;
  }) => {
    const fields: string[] = [];
    if (
      program.incentiveCategoryId === null ||
      program.incentiveCategoryId === undefined
    )
      fields.push("category");
    if (
      program.incentiveAreaId === null ||
      program.incentiveAreaId === undefined
    )
      fields.push("area");
    if (
      program.incentiveTypeId === null ||
      program.incentiveTypeId === undefined
    )
      fields.push("type");
    return fields;
  };

  // The API's ValidatePublishRequest adds these three on upsert and rejects the
  // save with a 400 when they are missing. SetPublishedAsync does not run them,
  // so only saveProgram checks this list.
  const missingPublishDetails = (program: {
    programUrl?: string | null;
    stateCode?: string | null;
    incentiveName?: string | null;
  }) => {
    const fields: string[] = [];
    if (!isAbsoluteHttpUrl(program.programUrl))
      fields.push("valid program URL");
    if ((program.stateCode ?? "").trim().length !== 2)
      fields.push("two-letter state code");
    if (!(program.incentiveName ?? "").trim()) fields.push("incentive name");
    return fields;
  };

  useEffect(() => {
    if (activeTab !== "users") return;
    const loadUsersAsync = async () => {
      setUserLoading(true);
      setStatus(null);
      try {
        const [usersResponse, customersResponse] = await Promise.all([
          api.Admin.getUsersAsync(),
          api.Admin.getCustomersAsync(),
        ]);
        if (usersResponse.zeosStatus?.toLowerCase() === "success") {
          setUsers(usersResponse.data ?? []);
        } else {
          throw new Error(usersResponse.message || "Failed to load users.");
        }
        if (customersResponse.zeosStatus?.toLowerCase() === "success") {
          setCustomers(customersResponse.data ?? []);
        } else {
          throw new Error(
            customersResponse.message || "Failed to load customers.",
          );
        }
      } catch (error: any) {
        log.error("Admin users load failed", error);
        setStatus({
          type: "error",
          message: error?.message || "Failed to load users.",
        });
      } finally {
        setUserLoading(false);
      }
    };
    loadUsersAsync();
  }, [activeTab, api.Admin, log]);

  useEffect(() => {
    if (activeTab !== "property-settings") return;

    const loadPropertySettingsAsync = async () => {
      setPropertySettingsLoading(true);
      setStatus(null);
      try {
        const customersResponse = await api.Admin.getCustomersAsync();
        if (customersResponse.zeosStatus?.toLowerCase() !== "success") {
          throw new Error(
            customersResponse.message || "Failed to load customers.",
          );
        }
        setCustomers(customersResponse.data ?? []);

        const propertyList = flattenProperties(customersResponse.data ?? []);
        const targetPropertyId =
          selectedPropertyId ??
          (propertyList.length > 0 ? propertyList[0].propertyId : null);

        setSelectedPropertyId(targetPropertyId);

        if (!targetPropertyId) {
          setPropertySettingsForm([]);
          return;
        }

        const settingsResponse =
          await api.Admin.getPropertySettingsAsync(targetPropertyId);
        if (settingsResponse.zeosStatus?.toLowerCase() !== "success") {
          throw new Error(
            settingsResponse.message || "Failed to load property settings.",
          );
        }

        setPropertySettingsForm(settingsResponse.data?.settings ?? []);
      } catch (error: any) {
        log.error("Property settings load failed", error);
        setStatus({
          type: "error",
          message: error?.message || "Failed to load property settings.",
        });
      } finally {
        setPropertySettingsLoading(false);
      }
    };

    loadPropertySettingsAsync();
  }, [activeTab, api.Admin, log, selectedPropertyId]);

  useEffect(() => {
    if (activeTab !== "customer-settings") return;

    const loadCustomerSettingsAsync = async () => {
      setCustomerSettingsLoading(true);
      setStatus(null);
      try {
        const customersResponse = await api.Admin.getCustomersAsync();
        if (customersResponse.zeosStatus?.toLowerCase() !== "success") {
          throw new Error(
            customersResponse.message || "Failed to load customers.",
          );
        }
        setCustomers(customersResponse.data ?? []);

        const targetCustomerId =
          selectedCustomerId ?? customersResponse.data?.[0]?.customerId ?? null;

        setSelectedCustomerId(targetCustomerId);

        if (!targetCustomerId) {
          setCustomerSettingsForm([]);
          return;
        }

        const settingsResponse =
          await api.Admin.getCustomerSettingsAsync(targetCustomerId);
        if (settingsResponse.zeosStatus?.toLowerCase() !== "success") {
          throw new Error(
            settingsResponse.message || "Failed to load customer settings.",
          );
        }

        setCustomerSettingsForm(settingsResponse.data?.settings ?? []);
      } catch (error: any) {
        log.error("Customer settings load failed", error);
        setStatus({
          type: "error",
          message: error?.message || "Failed to load customer settings.",
        });
      } finally {
        setCustomerSettingsLoading(false);
      }
    };

    loadCustomerSettingsAsync();
  }, [activeTab, api.Admin, log, selectedCustomerId]);

  useEffect(() => {
    if (activeTab !== "testing-sandbox") return;

    const loadSandboxPropertiesAsync = async () => {
      setSandboxActionLoading(true);
      setStatus(null);
      try {
        const customersResponse = await api.Admin.getCustomersAsync();
        if (customersResponse.zeosStatus?.toLowerCase() !== "success") {
          throw new Error(
            customersResponse.message || "Failed to load properties.",
          );
        }
        setCustomers(customersResponse.data ?? []);
      } catch (error: any) {
        log.error("Testing sandbox properties load failed", error);
        setStatus({
          type: "error",
          message: error?.message || "Failed to load properties.",
        });
      } finally {
        setSandboxActionLoading(false);
      }
    };

    loadSandboxPropertiesAsync();
  }, [activeTab, api.Admin, log]);

  useEffect(() => {
    if (activeTab !== "incentives") return;
    const loadIncentivesAsync = async () => {
      setProgramLoading(true);
      setDiagnosticsLoading(true);
      setStatus(null);
      try {
        const [programResult, referenceResult, diagnosticsResult] =
          await Promise.allSettled([
            api.IncentiveAdmin.getProgramsAsync(includeDrafts, includeArchived),
            api.IncentiveAdmin.getReferencesAsync(),
            api.IncentiveAdmin.getDiagnosticsAsync(),
          ]);

        if (programResult.status !== "fulfilled") {
          throw new Error("Failed to load incentive programs.");
        }

        const programResponse = programResult.value;
        if (programResponse.zeosStatus?.toLowerCase() === "success") {
          setPrograms(programResponse.data ?? []);
        } else {
          throw new Error(
            programResponse.message || "Failed to load incentive programs.",
          );
        }

        if (referenceResult.status === "fulfilled") {
          const referenceResponse = referenceResult.value;
          if (referenceResponse.zeosStatus?.toLowerCase() === "success") {
            setReferences(referenceResponse.data ?? null);
          } else {
            setReferences(null);
            setStatus({
              type: "error",
              message:
                referenceResponse.message ||
                "Incentive reference metadata failed to load. Listings remain available.",
            });
          }
        } else {
          setReferences(null);
          setStatus({
            type: "error",
            message:
              "Incentive reference metadata failed to load. Listings remain available.",
          });
        }

        if (diagnosticsResult.status === "fulfilled") {
          const diagnosticsResponse = diagnosticsResult.value;
          if (diagnosticsResponse.zeosStatus?.toLowerCase() === "success") {
            setDiagnostics(diagnosticsResponse.data ?? null);
          } else {
            setDiagnostics(null);
          }
        } else {
          setDiagnostics(null);
        }
      } catch (error: any) {
        log.error("Admin incentives load failed", error);
        setStatus({
          type: "error",
          message: error?.message || "Failed to load incentive programs.",
        });
      } finally {
        setProgramLoading(false);
        setDiagnosticsLoading(false);
      }
    };
    loadIncentivesAsync();
  }, [activeTab, api.IncentiveAdmin, includeArchived, includeDrafts, log]);

  const toggleUserSelection = (list: number[], id: number) => {
    return list.includes(id)
      ? list.filter((value) => value !== id)
      : [...list, id];
  };

  const flattenProperties = (
    list: IAdminCustomerResponse[],
  ): IAdminPropertyResponse[] => {
    return list.flatMap((customer) =>
      customer.companies.flatMap((company) =>
        company.properties.map((property) => ({
          ...property,
          name: `${property.name} (Company ${company.companyId})`,
        })),
      ),
    );
  };

  // Filters/compares on the raw property.name (not through flattenProperties, which appends
  // " (Company {id})" for its own dropdown-label callers) -- this tab needs the untouched name
  // both for the "QA" prefix match and the exact baseline-name match.
  const sandboxProperties = useMemo<SandboxPropertyRow[]>(() => {
    const rows = customers.flatMap((customer) =>
      customer.companies.flatMap((company) =>
        company.properties
          .filter(
            (property) =>
              property.name.trim().toUpperCase().startsWith("QA") ||
              property.isSandbox === true,
          )
          .map((property) => {
            const isBaseline =
              property.name.trim().toUpperCase() ===
              QA_BASELINE_PROPERTY_NAME.toUpperCase();
            const isSandbox = property.isSandbox === true;
            return {
              propertyId: property.propertyId,
              name: property.name,
              companyId: company.companyId,
              companyName: company.name,
              isBaseline,
              isSandbox,
              canReset: isSandbox,
              canDelete: isSandbox && !isBaseline,
            };
          }),
      ),
    );
    return rows.sort((a, b) =>
      a.isBaseline === b.isBaseline
        ? a.name.localeCompare(b.name)
        : a.isBaseline
          ? -1
          : 1,
    );
  }, [customers]);

  // The company picker is a flat list: no customer grouping, no properties. GetCustomers
  // already filters soft-deleted rows, so every company it returns is an active one.
  const companyOptions = useMemo(
    () =>
      customers
        .flatMap((customer) => customer.companies)
        .sort((a, b) => a.name.localeCompare(b.name)),
    [customers],
  );

  const submitUser = async () => {
    const trimmedEmail = userForm.emailAddress.trim();
    if (!trimmedEmail) {
      setStatus({ type: "error", message: "Email is required." });
      return;
    }
    //DISABLED per Greg 2026-10-01: environment email/domain gate removed from admin add-user.
    //NOTE: code-level change, so the gate is off here in ALL environments, prod included.
    // const emailCheck = handleZeosEnviromentEmailWhitelist(trimmedEmail);
    // if (!emailCheck.success) {
    //   setStatus({
    //     type: "error",
    //     message: emailCheck.message || "Invalid email address.",
    //   });
    //   return;
    // }

    // A non-super-admin reaches their portfolio through company membership, so creating one
    // with no company assignment would strand them. Super admins are scoped by their claim.
    if (!userForm.isSuperAdmin && userForm.companyIds.length === 0) {
      setStatus({
        type: "error",
        message: "Assign at least one company to this user.",
      });
      return;
    }

    setUserLoading(true);
    setStatus(null);
    try {
      const request: IAdminUserCreateRequest = {
        ...userForm,
        emailAddress: trimmedEmail,
        companyIds: userForm.isSuperAdmin ? [] : userForm.companyIds,
        // Properties are no longer selectable here; company assignment is the whole grant.
        propertyIds: [],
        companyRoleId: ADMIN_COMPANY_ROLE_ID,
      };
      const response = await api.Admin.createUserAsync(request);
      if (response.zeosStatus !== "success" || !response.data) {
        throw new Error(response.message || "User creation failed.");
      }
      setUsers((prev) => [response.data, ...prev]);
      setUserForm({
        firstName: "",
        lastName: "",
        emailAddress: "",
        companyIds: [],
        propertyIds: [],
        isSuperAdmin: false,
      });
      await zeosSuccess("User created and invite sent.", () => {});
    } catch (error: any) {
      setStatus({
        type: "error",
        message: error?.message || "User creation failed.",
      });
    } finally {
      setUserLoading(false);
    }
  };

  const toggleSuperAdmin = async (user: IAdminUserResponse) => {
    setUserLoading(true);
    setStatus(null);
    try {
      const response = await api.Admin.setSuperAdminAsync(user.zeosUserId, {
        isSuperAdmin: !user.isSuperAdmin,
      });
      if (response.zeosStatus !== "success" || !response.data) {
        throw new Error(
          response.message || "Failed to update super admin status.",
        );
      }
      setUsers((prev) =>
        prev.map((entry) =>
          entry.zeosUserId === user.zeosUserId ? response.data : entry,
        ),
      );
      setStatus({ type: "success", message: "Super admin status updated." });
    } catch (error: any) {
      setStatus({ type: "error", message: error?.message || "Update failed." });
    } finally {
      setUserLoading(false);
    }
  };

  const deleteUser = async (user: IAdminUserResponse) => {
    const firstConfirm = window.confirm(
      "Are you sure you want to nuke this account - data may be lost and this is audited that you did it!",
    );
    if (!firstConfirm) {
      return;
    }
    const secondConfirm = window.confirm(
      "Please confirm one more time to remove this account.",
    );
    if (!secondConfirm) {
      return;
    }

    setUserLoading(true);
    setStatus(null);
    try {
      const response = await api.Admin.deleteUserAsync(user.zeosUserId);
      if (response.zeosStatus !== "success" || response.data !== true) {
        throw new Error(response.message || "User deletion failed.");
      }
      setUsers((prev) =>
        prev.filter((entry) => entry.zeosUserId !== user.zeosUserId),
      );
      await zeosSuccess("User deleted.", () => {});
    } catch (error: any) {
      setStatus({
        type: "error",
        message: error?.message || "User deletion failed.",
      });
    } finally {
      setUserLoading(false);
    }
  };

  const renderPropertySettingValue = (
    setting: IAdminPropertySettingItem,
    index: number,
    definition: IPropertySettingKeyDefinition | null,
  ) => {
    const name = `setting-value-${index}`;

    if (definition?.kind === "triState") {
      return (
        <>
          <label className="block text-xs text-gray-600 mb-1" htmlFor={name}>
            Value
          </label>
          <select
            id={name}
            name={name}
            className="w-full border border-gray-300 rounded px-2 py-2 text-sm"
            value={normalizeTriStateValue(setting.settingValue)}
            onChange={(event) =>
              updatePropertySetting(index, "settingValue", event.target.value)
            }
          >
            {TRI_STATE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </>
      );
    }

    if (definition?.kind === "decimal") {
      return (
        <ZeosInput
          label="Value"
          name={name}
          type="number"
          value={setting.settingValue ?? ""}
          onChange={(event) =>
            updatePropertySetting(index, "settingValue", event.target.value)
          }
        />
      );
    }

    if (definition?.kind === "choice") {
      return (
        <>
          <label className="block text-xs text-gray-600 mb-1" htmlFor={name}>
            Value
          </label>
          <input
            id={name}
            name={name}
            className="w-full border border-gray-300 rounded px-2 py-2 text-sm"
            list={`property-setting-values-${index}`}
            value={setting.settingValue ?? ""}
            onChange={(event) =>
              updatePropertySetting(index, "settingValue", event.target.value)
            }
          />
          <datalist id={`property-setting-values-${index}`}>
            {(definition.options ?? []).map((option) => (
              <option
                key={option}
                value={option}
                label={definition.optionLabels?.[option]}
              />
            ))}
          </datalist>
        </>
      );
    }

    if (definition?.kind === "buildingUses") {
      const selectedUses = parseBuildingUses(setting.settingValue);
      return (
        <div>
          <label className="block text-xs text-gray-600 mb-1" htmlFor={name}>
            Building uses
          </label>
          <select
            id={name}
            name={name}
            multiple
            className="w-full min-h-28 border border-gray-300 rounded px-2 py-2 text-sm"
            value={selectedUses}
            onChange={(event) =>
              updatePropertySetting(
                index,
                "settingValue",
                serializeBuildingUses(
                  Array.from(event.target.selectedOptions, (option) => option.value),
                ),
              )
            }
          >
            {(definition.options ?? []).map((option) => (
              <option key={option} value={option}>
                {option === "mixed_use"
                  ? "Mixed use"
                  : option
                      .split("_")
                      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
                      .join(" ")}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-gray-500">
            Select every use present. Mixed use is a separate choice.
          </p>
        </div>
      );
    }

    return (
      <ZeosInput
        label="Value"
        name={name}
        value={setting.settingValue ?? ""}
        onChange={(event) =>
          updatePropertySetting(index, "settingValue", event.target.value)
        }
      />
    );
  };

  const renderPropertySettingsAdmin = () => {
    const propertyOptions = flattenProperties(customers);

    return (
      <div className="space-y-6">
        <div className="rounded border border-gray-200 p-4">
          <div className="mb-3 text-sm font-semibold text-gray-800">
            Property Settings
          </div>
          <p className="text-xs text-gray-500 mb-4">
            Manage per-property settings. Keys that drive incentive matching are
            offered as suggestions in the Key field and get a typed Value
            control. For Rock City, use key{" "}
            <span className="font-mono">Derapi:Rockcity:PropertyId</span>.
          </p>

          <datalist id="property-setting-key-suggestions">
            {PROPERTY_SETTING_KEY_SUGGESTIONS.map((key) => (
              <option key={key} value={key} />
            ))}
          </datalist>

          <label
            className="block text-xs text-gray-600 mb-1"
            htmlFor="property-settings-property"
          >
            Property
          </label>
          <select
            id="property-settings-property"
            className="w-full border border-gray-300 rounded px-2 py-2 text-sm"
            value={selectedPropertyId ?? ""}
            onChange={(event) => {
              const nextId = event.target.value
                ? Number(event.target.value)
                : null;
              setSelectedPropertyId(nextId);
            }}
            disabled={propertySettingsLoading}
          >
            <option value="">Select a property</option>
            {propertyOptions.map((property) => (
              <option key={property.propertyId} value={property.propertyId}>
                {property.name} (ID {property.propertyId})
              </option>
            ))}
          </select>

          {selectedPropertyId && (
            <div className="mt-4 space-y-3">
              {propertySettingsForm.length === 0 && (
                <div className="text-xs text-gray-500">No settings yet.</div>
              )}
              {propertySettingsForm.map((setting, index) => {
                const definition = findPropertySettingKey(setting.settingKey);
                const suggestion = suggestPropertySettingKey(
                  setting.settingKey,
                );

                return (
                  <div
                    key={`${setting.settingKey}-${index}`}
                    className="grid grid-cols-12 gap-3 items-end"
                  >
                    <div className="col-span-5">
                      <label
                        className="block text-xs text-gray-600 mb-1"
                        htmlFor={`setting-key-${index}`}
                      >
                        Key
                      </label>
                      <input
                        id={`setting-key-${index}`}
                        name={`setting-key-${index}`}
                        className="w-full border border-gray-300 rounded px-2 py-2 text-sm"
                        list="property-setting-key-suggestions"
                        value={setting.settingKey ?? ""}
                        onChange={(event) =>
                          updatePropertySetting(
                            index,
                            "settingKey",
                            event.target.value,
                          )
                        }
                      />
                    </div>
                    <div className="col-span-5">
                      {renderPropertySettingValue(setting, index, definition)}
                    </div>
                    <div className="col-span-2 flex gap-2">
                      <ZeosButton
                        label="Remove"
                        mode="action"
                        type="button"
                        onPress={() => removePropertySetting(index)}
                        isDisabled={propertySettingsLoading}
                        className="bg-gray-200 text-gray-700 hover:bg-gray-300 focus:ring-gray-400"
                      />
                    </div>
                    {suggestion && (
                      <p className="col-span-12 text-xs text-amber-700">
                        Unrecognised matching key. Did you mean{" "}
                        <span className="font-mono">{suggestion}</span>? A key
                        the matcher does not read saves fine but never gates.
                      </p>
                    )}
                    {definition && (
                      <p className="col-span-12 text-xs text-gray-500">
                        {definition.description}
                      </p>
                    )}
                    <PropertySettingProvenance setting={setting} />
                  </div>
                );
              })}

              <div className="flex gap-2">
                <ZeosButton
                  label="Add Setting"
                  mode="action"
                  type="button"
                  onPress={addPropertySetting}
                  isDisabled={propertySettingsLoading}
                  className="bg-slate-200 text-slate-800 hover:bg-slate-300 focus:ring-slate-400"
                />
                <ZeosButton
                  label={
                    propertySettingsLoading ? "Saving..." : "Save Settings"
                  }
                  mode="action"
                  type="button"
                  onPress={savePropertySettings}
                  isDisabled={propertySettingsLoading}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderCustomerSettingsAdmin = () => {
    return (
      <div className="space-y-6">
        <div className="rounded border border-gray-200 p-4">
          <div className="mb-3 text-sm font-semibold text-gray-800">
            Customer Settings
          </div>
          <p className="text-xs text-gray-500 mb-4">
            Manage per-customer settings (key/value). These are available for
            future use.
          </p>

          <label className="block text-xs text-gray-600 mb-1">Customer</label>
          <select
            className="w-full border border-gray-300 rounded px-2 py-2 text-sm"
            value={selectedCustomerId ?? ""}
            onChange={(event) => {
              const nextId = event.target.value
                ? Number(event.target.value)
                : null;
              setSelectedCustomerId(nextId);
            }}
            disabled={customerSettingsLoading}
          >
            <option value="">Select a customer</option>
            {customers.map((customer) => (
              <option key={customer.customerId} value={customer.customerId}>
                {customer.name} (ID {customer.customerId})
              </option>
            ))}
          </select>

          {selectedCustomerId && (
            <div className="mt-4 space-y-3">
              {customerSettingsForm.length === 0 && (
                <div className="text-xs text-gray-500">No settings yet.</div>
              )}
              {customerSettingsForm.map((setting, index) => (
                <div
                  key={`${setting.settingKey}-${index}`}
                  className="grid grid-cols-12 gap-3 items-end"
                >
                  <div className="col-span-5">
                    <ZeosInput
                      label="Key"
                      name={`customer-setting-key-${index}`}
                      value={setting.settingKey ?? ""}
                      onChange={(event) =>
                        updateCustomerSetting(
                          index,
                          "settingKey",
                          event.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="col-span-5">
                    <ZeosInput
                      label="Value"
                      name={`customer-setting-value-${index}`}
                      value={setting.settingValue ?? ""}
                      onChange={(event) =>
                        updateCustomerSetting(
                          index,
                          "settingValue",
                          event.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="col-span-2 flex gap-2">
                    <ZeosButton
                      label="Remove"
                      mode="action"
                      type="button"
                      onPress={() => removeCustomerSetting(index)}
                      isDisabled={customerSettingsLoading}
                      className="bg-gray-200 text-gray-700 hover:bg-gray-300 focus:ring-gray-400"
                    />
                  </div>
                </div>
              ))}

              <div className="flex gap-2">
                <ZeosButton
                  label="Add Setting"
                  mode="action"
                  type="button"
                  onPress={addCustomerSetting}
                  isDisabled={customerSettingsLoading}
                  className="bg-slate-200 text-slate-800 hover:bg-slate-300 focus:ring-slate-400"
                />
                <ZeosButton
                  label={
                    customerSettingsLoading ? "Saving..." : "Save Settings"
                  }
                  mode="action"
                  type="button"
                  onPress={saveCustomerSettings}
                  isDisabled={customerSettingsLoading}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderTestingSandboxAdmin = () => {
    // Only properties this account can actually open. The listing behind it is
    // super-admin and spans every customer, but the property endpoints scope by
    // customer role -- so cloning one of those produced a clone in the same
    // unreachable customer, which then 400s on open, edit and screen.
    const allProperties = flattenProperties(customers);
    const cloneSourceOptions = allProperties.filter(
      (property) => property.canRequesterAccess !== false,
    );
    const hiddenCloneSourceCount =
      allProperties.length - cloneSourceOptions.length;

    return (
    <div className="space-y-4">
      <p className="text-xs text-gray-500">
        QA screening-protocol properties (name starts with "QA") and sandbox
        clones. Clone any property into your own sandbox copy, then reset it back
        to whatever it was cloned from between variance tests, or delete it when
        you are done. The baseline itself cannot be edited, reset or deleted --
        clone it first.
      </p>

      <div className="rounded border border-gray-200 p-4">
        <div className="mb-1 text-sm font-semibold text-gray-800">
          Clone any property into the sandbox
        </div>
        <p className="mb-3 text-xs text-gray-500">
          Runs the protocol against a real portfolio property without seeding a
          fixture. The clone copies the property record, ESPM occupancy, HVAC,
          prior work, utility providers and the matching facts (OwnerClass,
          BuildingUses, IsHistoric...) -- so it screens as the same building as
          its source. Meters are deliberately excluded: they carry live
          data-connection credentials. Every clone can be reset to its own
          source, whether or not that source was {QA_BASELINE_PROPERTY_NAME}.
        </p>
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[18rem] flex-1">
            <label
              className="block text-xs text-gray-600 mb-1"
              htmlFor="sandbox-clone-source"
            >
              Property
            </label>
            <select
              id="sandbox-clone-source"
              className="w-full border border-gray-300 rounded px-2 py-2 text-sm"
              value={sandboxCloneSourceId ?? ""}
              onChange={(event) =>
                setSandboxCloneSourceId(
                  event.target.value ? Number(event.target.value) : null,
                )
              }
              disabled={sandboxActionLoading}
            >
              <option value="">Select a property</option>
              {cloneSourceOptions.map((property) => (
                <option key={property.propertyId} value={property.propertyId}>
                  {property.name} (ID {property.propertyId})
                </option>
              ))}
            </select>
            {hiddenCloneSourceCount > 0 && (
              <p className="mt-1 text-xs text-gray-500">
                {hiddenCloneSourceCount} propert
                {hiddenCloneSourceCount === 1 ? "y is" : "ies are"} not listed:
                they belong to a customer this account has no role on, so a clone
                of one could not be opened.
              </p>
            )}
          </div>
          <ZeosButton
            label="Clone into sandbox"
            mode="action"
            type="button"
            onPress={() => {
              if (sandboxCloneSourceId) {
                cloneSandboxProperty(sandboxCloneSourceId);
              }
            }}
            isDisabled={sandboxActionLoading || !sandboxCloneSourceId}
          />
        </div>
      </div>
      {sandboxActionLoading && sandboxProperties.length === 0 && (
        <p className="text-sm text-gray-600">Loading properties…</p>
      )}
      {!sandboxActionLoading && sandboxProperties.length === 0 && (
        <p className="text-sm text-gray-600">
          No QA-prefixed properties or sandbox clones found. Clone one above, or
          seed the {QA_BASELINE_PROPERTY_NAME} fixture.
        </p>
      )}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {sandboxProperties.map((property) => (
          <div
            key={property.propertyId}
            className="min-w-0 border border-gray-200 rounded p-4 space-y-2"
          >
            <div className="truncate text-sm font-semibold text-gray-900">
              {property.name}
            </div>
            <div className="text-xs text-gray-500">
              Property ID {property.propertyId} • {property.companyName}
              {property.isBaseline && (
                <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-amber-800">
                  Baseline (read-only)
                </span>
              )}
            </div>
            <div className="flex flex-wrap gap-2 pt-1">
              <ZeosButton
                label="Clone"
                mode="action"
                type="button"
                onPress={() => cloneSandboxProperty(property.propertyId)}
                isDisabled={sandboxActionLoading}
              />
              {property.canReset && (
                <ZeosButton
                  label="Reset to Source"
                  mode="action"
                  type="button"
                  onPress={() => resetSandboxProperty(property.propertyId)}
                  isDisabled={sandboxActionLoading}
                  className="bg-gray-200 text-gray-700 hover:bg-gray-300 focus:ring-gray-400"
                />
              )}
              {!property.isBaseline && (
                <ZeosButton
                  label="Edit Property"
                  mode="action"
                  type="button"
                  onPress={() => openEditSandboxProperty(property)}
                  isDisabled={sandboxActionLoading}
                  className="bg-slate-200 text-slate-800 hover:bg-slate-300 focus:ring-slate-400"
                />
              )}
              <ZeosButton
                label="Open Screening Sandbox"
                mode="action"
                type="button"
                onPress={() => navigate(`/screening-sandbox?propertyId=${property.propertyId}`)}
                isDisabled={sandboxActionLoading}
                className="bg-slate-200 text-slate-800 hover:bg-slate-300 focus:ring-slate-400"
              />
              {property.canDelete && (
                <ZeosButton
                  label="Delete"
                  mode="action"
                  type="button"
                  onPress={() => deleteSandboxProperty(property.propertyId, property.name)}
                  isDisabled={sandboxActionLoading}
                  className="bg-red-100 text-red-800 hover:bg-red-200 focus:ring-red-400"
                />
              )}
              <ZeosButton
                label="Export Building Profile"
                mode="action"
                type="button"
                onPress={() => exportPropertyProfile(property.propertyId, property.name)}
                isDisabled={sandboxActionLoading}
                className="bg-slate-200 text-slate-800 hover:bg-slate-300 focus:ring-slate-400"
              />
            </div>
          </div>
        ))}
      </div>

      <ZeosModal isOpen={editPropertyModalOpen} onClose={closeEditSandboxProperty}>
        <div className="max-h-[80vh] overflow-y-auto">
          <h2 className="text-lg font-semibold text-gray-900">
            Edit {editPropertyFormData.propertyName || "Property"}
          </h2>
          <p className="mt-1 text-xs text-gray-500">
            Saves directly to this sandbox clone's property record -- use Reset to Baseline
            afterward to undo.
          </p>
          {editPropertyError && (
            <div className="mt-3 rounded-md border border-red-200 bg-red-50 p-2 text-sm text-red-800">
              {editPropertyError}
            </div>
          )}
          {editPropertyLoading ? (
            <p className="mt-4 text-sm text-gray-600">Loading property…</p>
          ) : (
            <>
              <div className="mt-4 grid grid-cols-12 gap-y-3">
                <PropertyForm
                  formData={editPropertyFormData}
                  errors={editPropertyErrors}
                  handleChange={editPropertyFormChange}
                  setFormData={setEditPropertyFormData}
                  hvacOptions={sandboxHvacOptions}
                  priorWorkTypeOptions={sandboxPriorWorkTypeOptions}
                  showServingUtilitySelectors
                  showMatchingFacts
                  showSubmitButton={false}
                  disabled={editPropertySaving}
                />
              </div>
              <div className="mt-4 flex justify-end gap-2">
                <ZeosButton
                  label="Cancel"
                  mode="action"
                  type="button"
                  onPress={closeEditSandboxProperty}
                  isDisabled={editPropertySaving}
                  className="bg-gray-200 text-gray-700 hover:bg-gray-300 focus:ring-gray-400"
                />
                <ZeosButton
                  label={editPropertySaving ? "Saving…" : "Save"}
                  mode="action"
                  type="button"
                  onPress={saveEditSandboxProperty}
                  isDisabled={editPropertySaving}
                />
              </div>
            </>
          )}
        </div>
      </ZeosModal>
    </div>
    );
  };

  const updatePropertySetting = (
    index: number,
    field: "settingKey" | "settingValue",
    value: string,
  ) => {
    setPropertySettingsForm((prev) =>
      prev.map((item, idx) =>
        idx === index ? { ...item, [field]: value } : item,
      ),
    );
  };

  const addPropertySetting = () => {
    setPropertySettingsForm((prev) => [
      ...prev,
      { settingKey: "", settingValue: "" },
    ]);
  };

  const removePropertySetting = async (index: number) => {
    const target = propertySettingsForm[index];
    if (!target || !selectedPropertyId) {
      return;
    }

    if (target.settingKey?.trim()) {
      setPropertySettingsLoading(true);
      try {
        const response = await api.Admin.deletePropertySettingAsync(
          selectedPropertyId,
          target.settingKey,
        );
        if (response.zeosStatus?.toLowerCase() !== "success") {
          throw new Error(response.message || "Failed to delete setting.");
        }
      } catch (error: any) {
        setStatus({
          type: "error",
          message: error?.message || "Failed to delete setting.",
        });
        setPropertySettingsLoading(false);
        return;
      }
      await zeosSuccess("Property setting deleted.", () => {});
    }

    setPropertySettingsForm((prev) => prev.filter((_, idx) => idx !== index));
    setPropertySettingsLoading(false);
  };

  const savePropertySettings = async () => {
    if (!selectedPropertyId) {
      setStatus({ type: "error", message: "Select a property first." });
      return;
    }

    const payload = propertySettingsForm
      .map((item) => ({
        settingKey: item.settingKey?.trim(),
        settingValue: item.settingValue?.trim(),
      }))
      .filter((item) => item.settingKey);

    setPropertySettingsLoading(true);
    setStatus(null);
    try {
      const response = await api.Admin.upsertPropertySettingsAsync(
        selectedPropertyId,
        {
          settings: payload as IAdminPropertySettingItem[],
        },
      );
      if (response.zeosStatus?.toLowerCase() !== "success") {
        throw new Error(response.message || "Failed to save settings.");
      }
      setPropertySettingsForm(response.data?.settings ?? []);
      await zeosSuccess("Property settings saved.", () => {});
    } catch (error: any) {
      setStatus({
        type: "error",
        message: error?.message || "Failed to save settings.",
      });
    } finally {
      setPropertySettingsLoading(false);
    }
  };

  const updateCustomerSetting = (
    index: number,
    field: "settingKey" | "settingValue",
    value: string,
  ) => {
    setCustomerSettingsForm((prev) =>
      prev.map((item, idx) =>
        idx === index ? { ...item, [field]: value } : item,
      ),
    );
  };

  const addCustomerSetting = () => {
    setCustomerSettingsForm((prev) => [
      ...prev,
      { settingKey: "", settingValue: "" },
    ]);
  };

  const removeCustomerSetting = async (index: number) => {
    const target = customerSettingsForm[index];
    if (!target || !selectedCustomerId) {
      return;
    }

    if (target.settingKey?.trim()) {
      setCustomerSettingsLoading(true);
      try {
        const response = await api.Admin.deleteCustomerSettingAsync(
          selectedCustomerId,
          target.settingKey,
        );
        if (response.zeosStatus?.toLowerCase() !== "success") {
          throw new Error(response.message || "Failed to delete setting.");
        }
      } catch (error: any) {
        setStatus({
          type: "error",
          message: error?.message || "Failed to delete setting.",
        });
        setCustomerSettingsLoading(false);
        return;
      }
      await zeosSuccess("Customer setting deleted.", () => {});
    }

    setCustomerSettingsForm((prev) => prev.filter((_, idx) => idx !== index));
    setCustomerSettingsLoading(false);
  };

  const saveCustomerSettings = async () => {
    if (!selectedCustomerId) {
      setStatus({ type: "error", message: "Select a customer first." });
      return;
    }

    const payload = customerSettingsForm
      .map((item) => ({
        settingKey: item.settingKey?.trim(),
        settingValue: item.settingValue?.trim(),
      }))
      .filter((item) => item.settingKey);

    setCustomerSettingsLoading(true);
    setStatus(null);
    try {
      const response = await api.Admin.upsertCustomerSettingsAsync(
        selectedCustomerId,
        {
          settings: payload as IAdminCustomerSettingItem[],
        },
      );
      if (response.zeosStatus?.toLowerCase() !== "success") {
        throw new Error(response.message || "Failed to save settings.");
      }
      setCustomerSettingsForm(response.data?.settings ?? []);
      await zeosSuccess("Customer settings saved.", () => {});
    } catch (error: any) {
      setStatus({
        type: "error",
        message: error?.message || "Failed to save settings.",
      });
    } finally {
      setCustomerSettingsLoading(false);
    }
  };

  const cloneSandboxProperty = async (propertyId: number) => {
    setSandboxActionLoading(true);
    setStatus(null);
    try {
      const response = await api.Property.cloneToSandboxAsync(propertyId);
      if (response.zeosStatus?.toLowerCase() !== "success" || !response.data) {
        throw new Error(response.message || "Clone failed.");
      }
      const customersResponse = await api.Admin.getCustomersAsync();
      if (customersResponse.zeosStatus?.toLowerCase() === "success") {
        setCustomers(customersResponse.data ?? []);
      }
      setStatus({
        type: "success",
        message: `Sandbox copy "${response.data.propertyName}" created.`,
      });
    } catch (error: any) {
      setStatus({ type: "error", message: error?.message || "Clone failed." });
    } finally {
      setSandboxActionLoading(false);
    }
  };

  const deleteSandboxProperty = async (propertyId: number, name: string) => {
    if (
      !window.confirm(
        `Delete the sandbox property "${name}"? Its screening runs stay in the ` +
          `database for audit, but the property disappears from this tab and from ` +
          `the portfolio. This cannot be undone from the UI.`,
      )
    ) {
      return;
    }

    setSandboxActionLoading(true);
    setStatus(null);
    try {
      const response = await api.Property.deleteSandboxAsync(propertyId);
      if (response.zeosStatus?.toLowerCase() !== "success") {
        throw new Error(response.message || "Delete failed.");
      }
      const customersResponse = await api.Admin.getCustomersAsync();
      if (customersResponse.zeosStatus?.toLowerCase() === "success") {
        setCustomers(customersResponse.data ?? []);
      }
      setStatus({ type: "success", message: `Sandbox property "${name}" deleted.` });
    } catch (error: any) {
      setStatus({ type: "error", message: error?.message || "Delete failed." });
    } finally {
      setSandboxActionLoading(false);
    }
  };

  const resetSandboxProperty = async (propertyId: number) => {
    setSandboxActionLoading(true);
    setStatus(null);
    try {
      const response = await api.Property.resetToBaselineAsync(propertyId);
      if (response.zeosStatus?.toLowerCase() !== "success" || !response.data) {
        throw new Error(response.message || "Reset failed.");
      }
      setStatus({
        type: "success",
        message: `"${response.data.propertyName}" reset to baseline.`,
      });
    } catch (error: any) {
      setStatus({ type: "error", message: error?.message || "Reset failed." });
    } finally {
      setSandboxActionLoading(false);
    }
  };

  const ensureSandboxEditOptionsLoadedAsync = async () => {
    if (sandboxHvacOptions.length === 0) {
      const response = await api.ListUtility.getListAsync(
        ZeosListEnum.HvacPrimaryType,
        ZeosListOptionEnum.excludeUtility,
      );
      setSandboxHvacOptions(response.data ?? []);
    }
    if (sandboxPriorWorkTypeOptions.length === 0) {
      const response = await api.ListUtility.getListAsync(ZeosListEnum.BuildingPriorWorkType);
      setSandboxPriorWorkTypeOptions(
        response.data?.map((item) => ({ id: item.dataKey, name: item.display })) ?? [],
      );
    }
  };

  const openEditSandboxProperty = async (property: SandboxPropertyRow) => {
    setEditPropertyError(null);
    setEditPropertyCompanyId(property.companyId);
    setEditPropertyModalOpen(true);
    setEditPropertyLoading(true);
    try {
      await ensureSandboxEditOptionsLoadedAsync();
      const response = await api.Property.getProperty(property.propertyId);
      if (!response?.data) {
        throw new Error(response?.message || "Failed to load property.");
      }
      setEditPropertyFormData(mapPropertyContractToFormData(response.data, property.companyId));
    } catch (error: any) {
      setEditPropertyError(error?.message || "Failed to load property.");
    } finally {
      setEditPropertyLoading(false);
    }
  };

  const closeEditSandboxProperty = () => {
    setEditPropertyModalOpen(false);
    setEditPropertyError(null);
  };

  const saveEditSandboxProperty = async () => {
    const valid = await editPropertyFormValidate(async () => {
      setEditPropertySaving(true);
      setEditPropertyError(null);
      try {
        const payload = mapPropertyFormToContract(
          editPropertyFormData,
          editPropertyCompanyId ?? 0,
          editPropertyFormData.propertyId,
        );
        await api.Property.putProperty(payload);
        await zeosSuccess(`"${editPropertyFormData.propertyName}" saved.`, async () => {
          setEditPropertyModalOpen(false);
          const customersResponse = await api.Admin.getCustomersAsync();
          if (customersResponse.zeosStatus?.toLowerCase() === "success") {
            setCustomers(customersResponse.data ?? []);
          }
        });
      } catch (error: any) {
        setEditPropertyError(error?.message || "Failed to save property.");
      } finally {
        setEditPropertySaving(false);
      }
    });
    if (!valid) {
      setEditPropertyError("Fix the highlighted fields before saving.");
    }
  };

  // Pre-run export (Protocol §11 ask 4 / QT-6): lets a tester attach the exact building profile
  // to a defect report before a screening run completes, or when a run fails outright -- the
  // post-run counterpart is the "Export Building Profile" control on the results view, which
  // downloads Screening/Runs/Guid/{runGuid}/IncentiveMatchRequest instead of the raw property row.
  const exportPropertyProfile = async (propertyId: number, propertyName: string) => {
    setSandboxActionLoading(true);
    setStatus(null);
    try {
      const response = await api.Property.getProperty(propertyId);
      if (!response?.data) {
        throw new Error(response?.message || "Unable to load property.");
      }
      const safeName = propertyName.replace(/[^a-zA-Z0-9-_]+/g, "-");
      const blob = new Blob([JSON.stringify(response.data, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${safeName}-building-profile.json`;
      link.click();
      URL.revokeObjectURL(url);
      setStatus({
        type: "success",
        message: `Exported building profile for "${propertyName}".`,
      });
    } catch (error: any) {
      setStatus({ type: "error", message: error?.message || "Export failed." });
    } finally {
      setSandboxActionLoading(false);
    }
  };

  // The status banner renders inside the editor while it is open, so a message
  // from an earlier program must not carry into the next one or onto the page.
  const resetProgramForm = () => {
    setProgramForm(buildEmptyProgram());
    setStatus(null);
    setProgramEditorOpen(true);
  };

  const openProgramEditor = (program: IAdminIncentiveProgramResponse) => {
    setProgramForm({
      ...program,
      startDate: formatDateInput(program.startDate),
      endDate: formatDateInput(program.endDate),
    });
    setStatus(null);
    setProgramEditorOpen(true);
  };

  const closeProgramEditor = () => {
    setStatus(null);
    setProgramEditorOpen(false);
  };

  const duplicateProgramAsDraft = (program: IAdminIncentiveProgramResponse) => {
    setProgramForm({
      ...program,
      incentiveProgramId: null,
      incentiveId: null,
      name: `${program.name} Copy`,
      incentiveName: program.incentiveName
        ? `${program.incentiveName} Copy`
        : "",
      isPublished: false,
      isDeleted: false,
      startDate: formatDateInput(program.startDate),
      endDate: formatDateInput(program.endDate),
    });
    setStatus(null);
    setProgramEditorOpen(true);
  };

  const refreshDiagnostics = async (showStatus = true) => {
    setDiagnosticsLoading(true);
    try {
      const response = await api.IncentiveAdmin.getDiagnosticsAsync();
      if (response.zeosStatus?.toLowerCase() !== "success") {
        throw new Error(response.message || "Diagnostics failed.");
      }
      setDiagnostics(response.data ?? null);
      if (showStatus) {
        setStatus({ type: "success", message: "Diagnostics refreshed." });
      }
    } catch (error: any) {
      setStatus({
        type: "error",
        message: error?.message || "Diagnostics failed.",
      });
    } finally {
      setDiagnosticsLoading(false);
    }
  };

  const exportProgramsCsv = () => {
    const headers = [
      "Program ID",
      "Incentive ID",
      "Program",
      "Incentive",
      "Source",
      "Category",
      "Area",
      "Type",
      "State",
      "Published",
      "Archived",
      "Updated",
      "URL",
    ];
    const escapeCsv = (value: unknown) =>
      `"${String(value ?? "").replace(/"/g, '""')}"`;
    const rows = filteredPrograms.map((program) => [
      program.incentiveProgramId,
      program.incentiveId ?? "",
      program.name,
      program.incentiveName ?? "",
      referenceName(references?.sources, program.incentiveSourceId),
      referenceName(references?.categories, program.incentiveCategoryId),
      referenceName(references?.areas, program.incentiveAreaId),
      referenceName(references?.types, program.incentiveTypeId),
      program.stateCode ?? "",
      program.isPublished ? "Published" : "Draft",
      program.isDeleted ? "Archived" : "Active",
      program.updatedDate ?? "",
      program.programUrl ?? "",
    ]);
    const csv = [headers, ...rows]
      .map((row) => row.map(escapeCsv).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "incentive-programs.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  const saveProgram = async () => {
    if (!programForm.name.trim()) {
      setStatus({ type: "error", message: "Program name is required." });
      return;
    }
    if (
      programForm.incentiveSourceId === null ||
      programForm.incentiveSourceId === undefined
    ) {
      setStatus({ type: "error", message: "Incentive source is required." });
      return;
    }
    if (programForm.requirementsJson?.trim()) {
      try {
        JSON.parse(programForm.requirementsJson);
      } catch {
        setStatus({
          type: "error",
          message: "Requirements vector must be valid JSON.",
        });
        return;
      }
    }
    const requirementsJson = mergeIncentiveRequirementsIdentity(
      programForm.requirementsJson,
      {
        programId: programForm.incentiveProgramId,
        incentiveId: programForm.incentiveId,
        programName: programForm.name,
        stateCode: programForm.stateCode,
      },
    );
    // saveProgram sends the program name when no incentive name is given, so
    // validate the value that actually goes to the API.
    const effectiveIncentiveName =
      programForm.incentiveName?.trim() || programForm.name.trim();
    const missingFields = programForm.isPublished
      ? [
          ...missingPublishMetadata(programForm),
          ...missingPublishDetails({
            ...programForm,
            incentiveName: effectiveIncentiveName,
          }),
        ]
      : [];
    if (missingFields.length > 0) {
      setStatus({
        type: "error",
        message: `Published incentive programs require ${missingFields.join(", ")}.`,
      });
      return;
    }

    setProgramLoading(true);
    setStatus(null);
    try {
      const response = await api.IncentiveAdmin.upsertProgramAsync({
        ...programForm,
        incentiveName:
          programForm.incentiveName?.trim() || programForm.name.trim(),
        requirementsJson,
        startDate: programForm.startDate || null,
        endDate: programForm.endDate || null,
      });
      if (response.zeosStatus?.toLowerCase() !== "success" || !response.data) {
        throw new Error(response.message || "Save failed.");
      }
      setPrograms((prev) => {
        const exists = prev.some(
          (entry) =>
            entry.incentiveProgramId === response.data!.incentiveProgramId,
        );
        if (exists) {
          return prev.map((entry) =>
            entry.incentiveProgramId === response.data!.incentiveProgramId
              ? response.data!
              : entry,
          );
        }
        return [response.data!, ...prev];
      });
      setProgramForm({
        ...response.data,
        startDate: formatDateInput(response.data.startDate),
        endDate: formatDateInput(response.data.endDate),
      });
      setProgramEditorOpen(false);
      await zeosSuccess("Incentive program saved.", () => {});
      void refreshDiagnostics(false);
    } catch (error: any) {
      setStatus({ type: "error", message: error?.message || "Save failed." });
    } finally {
      setProgramLoading(false);
    }
  };

  const setPublishState = async (
    program: IAdminIncentiveProgramResponse,
    value: boolean,
  ) => {
    const missingFields = value ? missingPublishMetadata(program) : [];
    if (missingFields.length > 0) {
      setStatus({
        type: "error",
        message: `Published incentive programs require ${missingFields.join(", ")}.`,
      });
      return;
    }

    setProgramLoading(true);
    setStatus(null);
    try {
      const response = await api.IncentiveAdmin.setPublishedAsync(
        program.incentiveProgramId,
        {
          isPublished: value,
        },
      );
      if (response.zeosStatus?.toLowerCase() !== "success" || !response.data) {
        throw new Error(response.message || "Publish update failed.");
      }
      setPrograms((prev) =>
        prev.map((entry) =>
          entry.incentiveProgramId === response.data!.incentiveProgramId
            ? response.data!
            : entry,
        ),
      );
      setProgramForm({
        ...response.data,
        startDate: formatDateInput(response.data.startDate),
        endDate: formatDateInput(response.data.endDate),
      });
      setStatus({
        type: "success",
        message: value ? "Program published." : "Program moved to draft.",
      });
      void refreshDiagnostics(false);
    } catch (error: any) {
      setStatus({
        type: "error",
        message: error?.message || "Publish update failed.",
      });
    } finally {
      setProgramLoading(false);
    }
  };

  const archiveProgram = async (program: IAdminIncentiveProgramResponse) => {
    if (!window.confirm(`Archive ${program.name}?`)) {
      return;
    }
    setProgramLoading(true);
    setStatus(null);
    try {
      const response = await api.IncentiveAdmin.deleteProgramAsync(
        program.incentiveProgramId,
      );
      if (response.zeosStatus?.toLowerCase() !== "success") {
        throw new Error(response.message || "Archive failed.");
      }
      setPrograms((prev) =>
        prev.filter(
          (entry) => entry.incentiveProgramId !== program.incentiveProgramId,
        ),
      );
      setProgramForm(buildEmptyProgram());
      closeProgramEditor();
      await zeosSuccess("Program archived.", () => {});
      void refreshDiagnostics(false);
    } catch (error: any) {
      setStatus({
        type: "error",
        message: error?.message || "Archive failed.",
      });
    } finally {
      setProgramLoading(false);
    }
  };

  const restoreProgram = async (program: IAdminIncentiveProgramResponse) => {
    setProgramLoading(true);
    setStatus(null);
    try {
      const response = await api.IncentiveAdmin.restoreProgramAsync(
        program.incentiveProgramId,
      );
      if (response.zeosStatus?.toLowerCase() !== "success" || !response.data) {
        throw new Error(response.message || "Restore failed.");
      }
      setPrograms((prev) =>
        prev.map((entry) =>
          entry.incentiveProgramId === response.data!.incentiveProgramId
            ? response.data!
            : entry,
        ),
      );
      await zeosSuccess("Program restored.", () => {});
      void refreshDiagnostics(false);
    } catch (error: any) {
      setStatus({
        type: "error",
        message: error?.message || "Restore failed.",
      });
    } finally {
      setProgramLoading(false);
    }
  };

  const runResearch = async () => {
    const limit = Math.min(Math.max(researchForm.limit || 20, 1), 50);
    setResearchLoading(true);
    setStatus(null);
    try {
      const response = await api.IncentiveAdmin.researchAsync(
        { ...researchForm, limit },
        limit,
      );
      if (response.zeosStatus?.toLowerCase() !== "success") {
        throw new Error(response.message || "Research failed.");
      }
      setResearchResults(response.data?.results ?? []);
      setStatus({ type: "success", message: "Research complete." });
    } catch (error: any) {
      setStatus({
        type: "error",
        message: error?.message || "Research failed.",
      });
    } finally {
      setResearchLoading(false);
    }
  };

  const applyResearchResult = (result: IIncentiveResearchResult) => {
    const category = resolveReferenceId(
      references?.categories,
      result.category,
      incentiveCategoryAliases,
    );
    const area = resolveReferenceId(
      references?.areas,
      result.area,
      incentiveAreaAliases,
    );
    const type = resolveReferenceId(
      references?.types,
      result.type,
      incentiveTypeAliases,
    );
    const fallbackLabels = [
      category.usedFallback ? `category: ${category.label}` : "",
      area.usedFallback ? `area: ${area.label}` : "",
      type.usedFallback ? `type: ${type.label}` : "",
    ].filter(Boolean);

    setProgramForm({
      ...buildEmptyProgram(),
      incentiveProgramId: null,
      incentiveId: null,
      name: result.programName ?? "",
      programUrl: result.programUrl ?? "",
      description: result.description ?? "",
      programStatus: result.programStatus ?? "",
      incentiveCategoryId: category.id,
      incentiveAreaId: area.id,
      incentiveTypeId: type.id,
      isDacOnly: null,
      isPublished: false,
      incentiveName: result.incentiveName ?? "",
      incentiveDescription: result.incentiveDescription ?? "",
      maxIncentiveAmount: result.maxIncentiveAmount ?? null,
      incentiveRate: result.incentiveRate ?? null,
      incentiveRateUnit: result.incentiveRateUnit ?? "",
      costSharePercent: result.costSharePercent ?? null,
      requirementsJson: buildIncentiveRequirementsSkeleton({
        programName: result.programName ?? "",
      }),
    });
    setProgramEditorOpen(true);
    setStatus({
      type: fallbackLabels.length > 0 ? "error" : "success",
      message:
        fallbackLabels.length > 0
          ? `Research result staged as draft. Review fallback metadata for ${fallbackLabels.join(", ")}.`
          : "Research result staged as draft.",
    });
  };

  const renderUserAdmin = () => (
    <div className="grid grid-cols-12 gap-6">
      <div className="col-span-12 xl:col-span-7 bg-white border border-gray-200 rounded p-4">
        <h3 className="text-sm font-semibold text-black mb-3">Users</h3>
        {userLoading && <p className="text-sm text-gray-600">Loading users…</p>}
        {!userLoading && users.length === 0 && (
          <p className="text-sm text-gray-600">No users found.</p>
        )}
        {!userLoading && users.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm border border-gray-200">
              <thead className="bg-gray-100 text-left">
                <tr>
                  <th className="px-3 py-2 border-b">Name</th>
                  <th className="px-3 py-2 border-b">Email</th>
                  <th className="px-3 py-2 border-b">Super Admin</th>
                  <th className="px-3 py-2 border-b">Account</th>
                  <th className="px-3 py-2 border-b">Action</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.zeosUserId} className="border-t">
                    <td className="ph-mask px-3 py-2">{`${user.firstName} ${user.lastName}`}</td>
                    <td className="ph-mask px-3 py-2">{user.emailAddress}</td>
                    <td className="px-3 py-2">
                      {user.isSuperAdmin ? "Yes" : "No"}
                    </td>
                    <td className="px-3 py-2">
                      {user.hasAccount ? "Active" : "Invite Pending"}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          className="text-xs text-zeos-orange-dk hover:underline"
                          onClick={() => toggleSuperAdmin(user)}
                          disabled={userLoading}
                        >
                          {user.isSuperAdmin
                            ? "Remove Super Admin"
                            : "Make Super Admin"}
                        </button>
                        <button
                          type="button"
                          className="text-xs text-red-600 hover:underline"
                          onClick={() => deleteUser(user)}
                          disabled={userLoading}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="col-span-12 xl:col-span-5 bg-white border border-gray-200 rounded p-4">
        <h3 className="text-sm font-semibold text-black mb-3">Create User</h3>
        <div className="grid grid-cols-12 gap-3 text-sm">
          <div className="col-span-6">
            <label className="block text-xs text-gray-600 mb-1">
              First Name
            </label>
            <input
              value={userForm.firstName}
              onChange={(event) =>
                setUserForm({ ...userForm, firstName: event.target.value })
              }
              className="w-full border border-gray-300 rounded px-2 py-2"
            />
          </div>
          <div className="col-span-6">
            <label className="block text-xs text-gray-600 mb-1">
              Last Name
            </label>
            <input
              value={userForm.lastName}
              onChange={(event) =>
                setUserForm({ ...userForm, lastName: event.target.value })
              }
              className="w-full border border-gray-300 rounded px-2 py-2"
            />
          </div>
          <div className="col-span-12">
            <label className="block text-xs text-gray-600 mb-1">Email</label>
            <input
              value={userForm.emailAddress}
              onChange={(event) =>
                setUserForm({ ...userForm, emailAddress: event.target.value })
              }
              className="w-full border border-gray-300 rounded px-2 py-2"
            />
          </div>
          <div className="col-span-12 flex items-center gap-2">
            <input
              type="checkbox"
              checked={userForm.isSuperAdmin}
              onChange={() =>
                setUserForm({
                  ...userForm,
                  isSuperAdmin: !userForm.isSuperAdmin,
                })
              }
            />
            <span className="text-xs text-gray-700">Super administrator</span>
          </div>

          {!userForm.isSuperAdmin && (
            <div className="col-span-12">
              <div className="text-xs text-gray-600 mb-2">
                Assign Companies to this user
              </div>
              <div className="space-y-1 max-h-64 overflow-y-auto border border-gray-200 rounded p-2">
                {companyOptions.length === 0 && (
                  <div className="text-xs text-gray-500">
                    No companies available.
                  </div>
                )}
                {companyOptions.map((company) => (
                  <label
                    key={company.companyId}
                    className="flex items-center gap-2 text-xs text-gray-700"
                  >
                    <input
                      type="checkbox"
                      checked={userForm.companyIds.includes(company.companyId)}
                      onChange={() =>
                        setUserForm({
                          ...userForm,
                          companyIds: toggleUserSelection(
                            userForm.companyIds,
                            company.companyId,
                          ),
                        })
                      }
                    />
                    {company.name}
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={submitUser}
          className="mt-4 bg-zeos-orange-dk text-white px-4 py-2 rounded text-sm"
          disabled={userLoading}
        >
          {userLoading ? "Saving..." : "Create User"}
        </button>
      </div>
    </div>
  );

  const renderStatusBanner = (className = "") =>
    status ? (
      <div
        role={status.type === "success" ? "status" : "alert"}
        className={`text-xs ${className} ${
          status.type === "success" ? "text-green-600" : "text-red-600"
        }`}
      >
        {status.message}
      </div>
    ) : null;

  const renderIncentiveEditor = () => (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={
        programForm.incentiveProgramId
          ? "Edit Incentive Program"
          : "Create Incentive Program"
      }
      className="fixed inset-0 z-40 bg-white"
    >
      {/* Size from the fixed parent, never from viewport units: iOS resolves
          100vh to the LARGE viewport (toolbar retracted), which makes this
          shell taller than the visible area and pushes the footer -- Save and
          the status banner -- below the fold with no way to scroll to it. The
          parent's `fixed inset-0` already tracks the visible viewport. */}
      <div
        data-testid="incentive-editor-shell"
        className="flex h-full w-full flex-col overflow-hidden bg-white"
      >
        <div className="flex shrink-0 items-center justify-between border-b border-gray-200 bg-white px-4 py-3 md:px-6">
          <div>
            <h3 className="text-base font-semibold text-black">
              {programForm.incentiveProgramId
                ? "Edit Incentive Program"
                : "Create Incentive Program"}
            </h3>
            <div className="mt-1 text-xs text-gray-500">
              {programForm.incentiveProgramId
                ? `Program ID ${programForm.incentiveProgramId}`
                : "Draft record"}
            </div>
          </div>
          <button
            type="button"
            className="rounded border border-gray-300 px-3 py-1.5 text-sm text-gray-700"
            onClick={closeProgramEditor}
          >
            Close
          </button>
        </div>

        <div className="grid flex-1 grid-cols-12 gap-4 overflow-y-auto p-4 text-sm md:p-6">
          <div className="col-span-12 text-xs font-semibold uppercase text-gray-500">
            Program Identity
          </div>
          <div className="col-span-12 xl:col-span-5">
            <label
              className="block text-xs text-gray-600 mb-1"
              htmlFor="incentive-program-name"
            >
              Program Name
            </label>
            <input
              id="incentive-program-name"
              value={programForm.name}
              onChange={(event) =>
                setProgramForm({ ...programForm, name: event.target.value })
              }
              className="w-full border border-gray-300 rounded px-2 py-2"
            />
          </div>
          <div className="col-span-12 md:col-span-8 xl:col-span-5">
            <label className="block text-xs text-gray-600 mb-1">
              Program URL
            </label>
            <input
              value={programForm.programUrl || ""}
              onChange={(event) =>
                setProgramForm({
                  ...programForm,
                  programUrl: event.target.value,
                })
              }
              className="w-full border border-gray-300 rounded px-2 py-2"
            />
          </div>
          <div className="col-span-12 md:col-span-4 xl:col-span-2">
            <label className="block text-xs text-gray-600 mb-1">
              Program Status
            </label>
            <input
              value={programForm.programStatus || ""}
              onChange={(event) =>
                setProgramForm({
                  ...programForm,
                  programStatus: event.target.value,
                })
              }
              className="w-full border border-gray-300 rounded px-2 py-2"
            />
          </div>
          <div className="col-span-12 xl:col-span-6">
            <label className="block text-xs text-gray-600 mb-1">
              Description
            </label>
            <textarea
              value={programForm.description || ""}
              onChange={(event) =>
                setProgramForm({
                  ...programForm,
                  description: event.target.value,
                })
              }
              className="w-full border border-gray-300 rounded px-2 py-2"
              rows={3}
            />
          </div>

          <div className="col-span-12 mt-3 text-xs font-semibold uppercase text-gray-500 xl:col-span-6 xl:mt-0">
            Classification
          </div>
          <div className="col-span-12 md:col-span-4 xl:col-span-3">
            <label
              className="block text-xs text-gray-600 mb-1"
              htmlFor="incentive-program-source"
            >
              Source
            </label>
            <select
              id="incentive-program-source"
              value={programForm.incentiveSourceId ?? ""}
              onChange={(event) =>
                setProgramForm({
                  ...programForm,
                  incentiveSourceId: event.target.value
                    ? Number(event.target.value)
                    : null,
                })
              }
              className="w-full border border-gray-300 rounded px-2 py-2"
            >
              <option value="">Select source</option>
              {referenceOptions(
                references?.sources,
                programForm.incentiveSourceId,
              ).map((source) => (
                <option key={source.id} value={source.id}>
                  {source.name}
                </option>
              ))}
            </select>
          </div>
          <div className="col-span-12 md:col-span-2 xl:col-span-2">
            <label className="block text-xs text-gray-600 mb-1">Category</label>
            <select
              value={programForm.incentiveCategoryId ?? ""}
              onChange={(event) =>
                setProgramForm({
                  ...programForm,
                  incentiveCategoryId: event.target.value
                    ? Number(event.target.value)
                    : null,
                })
              }
              className="w-full border border-gray-300 rounded px-2 py-2"
            >
              <option value="">Select</option>
              {referenceOptions(
                references?.categories,
                programForm.incentiveCategoryId,
              ).map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
          </div>
          <div className="col-span-12 md:col-span-2 xl:col-span-2">
            <label className="block text-xs text-gray-600 mb-1">Area</label>
            <select
              value={programForm.incentiveAreaId ?? ""}
              onChange={(event) =>
                setProgramForm({
                  ...programForm,
                  incentiveAreaId: event.target.value
                    ? Number(event.target.value)
                    : null,
                })
              }
              className="w-full border border-gray-300 rounded px-2 py-2"
            >
              <option value="">Select</option>
              {referenceOptions(
                references?.areas,
                programForm.incentiveAreaId,
              ).map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
          </div>
          <div className="col-span-12 md:col-span-2 xl:col-span-2">
            <label className="block text-xs text-gray-600 mb-1">Type</label>
            <select
              value={programForm.incentiveTypeId ?? ""}
              onChange={(event) =>
                setProgramForm({
                  ...programForm,
                  incentiveTypeId: event.target.value
                    ? Number(event.target.value)
                    : null,
                })
              }
              className="w-full border border-gray-300 rounded px-2 py-2"
            >
              <option value="">Select</option>
              {referenceOptions(
                references?.types,
                programForm.incentiveTypeId,
              ).map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
          </div>
          <div className="col-span-12 md:col-span-2 xl:col-span-1">
            <label
              className="block text-xs text-gray-600 mb-1"
              htmlFor="incentive-program-state"
            >
              State
            </label>
            <input
              id="incentive-program-state"
              value={programForm.stateCode || ""}
              onChange={(event) =>
                setProgramForm({
                  ...programForm,
                  stateCode: event.target.value.toUpperCase(),
                })
              }
              maxLength={2}
              className="w-full border border-gray-300 rounded px-2 py-2"
            />
            <p className="mt-1 text-xs text-gray-500">
              Leave blank for federal.
            </p>
          </div>
          <div className="col-span-12 flex flex-wrap gap-5 xl:col-span-2 xl:items-end">
            <label className="flex items-center gap-2 text-xs text-gray-700">
              <input
                type="checkbox"
                checked={Boolean(programForm.isDacOnly)}
                onChange={() =>
                  setProgramForm({
                    ...programForm,
                    isDacOnly: !programForm.isDacOnly,
                  })
                }
              />
              DAC only
            </label>
            <label className="flex items-center gap-2 text-xs text-gray-700">
              <input
                type="checkbox"
                checked={programForm.isPublished}
                onChange={() =>
                  setProgramForm({
                    ...programForm,
                    isPublished: !programForm.isPublished,
                  })
                }
              />
              Published
            </label>
          </div>
          <div className="col-span-12 md:col-span-6 xl:col-span-3">
            <label className="block text-xs text-gray-600 mb-1">
              Start Date
            </label>
            <input
              type="date"
              value={programForm.startDate || ""}
              onChange={(event) =>
                setProgramForm({
                  ...programForm,
                  startDate: event.target.value,
                })
              }
              className="w-full border border-gray-300 rounded px-2 py-2"
            />
          </div>
          <div className="col-span-12 md:col-span-6 xl:col-span-3">
            <label className="block text-xs text-gray-600 mb-1">End Date</label>
            <input
              type="date"
              value={programForm.endDate || ""}
              onChange={(event) =>
                setProgramForm({ ...programForm, endDate: event.target.value })
              }
              className="w-full border border-gray-300 rounded px-2 py-2"
            />
          </div>

          <div className="col-span-12 mt-3 text-xs font-semibold uppercase text-gray-500">
            Incentive Detail
          </div>
          <div className="col-span-12 xl:col-span-6">
            <label className="block text-xs text-gray-600 mb-1">
              Incentive Name
            </label>
            <input
              value={programForm.incentiveName || ""}
              onChange={(event) =>
                setProgramForm({
                  ...programForm,
                  incentiveName: event.target.value,
                })
              }
              className="w-full border border-gray-300 rounded px-2 py-2"
            />
          </div>
          <div className="col-span-12 xl:col-span-6">
            <label className="block text-xs text-gray-600 mb-1">
              Incentive Description
            </label>
            <textarea
              value={programForm.incentiveDescription || ""}
              onChange={(event) =>
                setProgramForm({
                  ...programForm,
                  incentiveDescription: event.target.value,
                })
              }
              className="w-full border border-gray-300 rounded px-2 py-2"
              rows={3}
            />
          </div>
          <div className="col-span-6 md:col-span-3">
            <label className="block text-xs text-gray-600 mb-1">
              Max Amount
            </label>
            <input
              type="number"
              value={programForm.maxIncentiveAmount ?? ""}
              onChange={(event) =>
                setProgramForm({
                  ...programForm,
                  maxIncentiveAmount: event.target.value
                    ? Number(event.target.value)
                    : null,
                })
              }
              className="w-full border border-gray-300 rounded px-2 py-2"
            />
          </div>
          <div className="col-span-6 md:col-span-3">
            <label className="block text-xs text-gray-600 mb-1">Rate</label>
            <input
              type="number"
              value={programForm.incentiveRate ?? ""}
              onChange={(event) =>
                setProgramForm({
                  ...programForm,
                  incentiveRate: event.target.value
                    ? Number(event.target.value)
                    : null,
                })
              }
              className="w-full border border-gray-300 rounded px-2 py-2"
            />
          </div>
          <div className="col-span-6 md:col-span-3">
            <label className="block text-xs text-gray-600 mb-1">
              Rate Unit
            </label>
            <input
              value={programForm.incentiveRateUnit || ""}
              onChange={(event) =>
                setProgramForm({
                  ...programForm,
                  incentiveRateUnit: event.target.value,
                })
              }
              className="w-full border border-gray-300 rounded px-2 py-2"
            />
          </div>
          <div className="col-span-6 md:col-span-3">
            <label className="block text-xs text-gray-600 mb-1">
              Cost Share %
            </label>
            <input
              type="number"
              value={programForm.costSharePercent ?? ""}
              onChange={(event) =>
                setProgramForm({
                  ...programForm,
                  costSharePercent: event.target.value
                    ? Number(event.target.value)
                    : null,
                })
              }
              className="w-full border border-gray-300 rounded px-2 py-2"
            />
          </div>

          <IncentiveEligibilityVectorFields
            requirementsJson={programForm.requirementsJson}
            stateCode={programForm.stateCode}
            onChange={(nextJson) =>
              setProgramForm({
                ...programForm,
                requirementsJson: nextJson,
              })
            }
          />
        </div>

        <div
          data-testid="incentive-editor-footer"
          className="shrink-0 border-t border-gray-200 bg-white px-4 py-3 md:px-6"
        >
          {renderStatusBanner("mb-2")}
          <div className="flex items-center justify-between">
            <button
              type="button"
              className="text-sm text-gray-600 hover:underline"
              onClick={closeProgramEditor}
            >
              Cancel
            </button>
            <div className="flex items-center gap-3">
              {ENABLE_INCENTIVE_PROGRAM_DUPLICATE &&
                programForm.incentiveProgramId &&
                !programForm.isDeleted && (
                  <button
                    type="button"
                    className="rounded border border-gray-300 px-4 py-2 text-sm text-gray-700"
                    onClick={() =>
                      duplicateProgramAsDraft(
                        programForm as IAdminIncentiveProgramResponse,
                      )
                    }
                  >
                    Duplicate Draft
                  </button>
                )}
              <button
                type="button"
                className="bg-zeos-orange-dk text-white px-4 py-2 rounded text-sm"
                onClick={saveProgram}
                disabled={programLoading || programForm.isDeleted}
              >
                {programLoading ? "Saving..." : "Save Program"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderIncentiveAdmin = () => (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-6">
        {[
          [
            "Active",
            diagnostics?.totalActivePrograms ??
              programs.filter((item) => !item.isDeleted).length,
          ],
          [
            "Published",
            diagnostics?.totalPublishedPrograms ??
              programs.filter((item) => item.isPublished && !item.isDeleted)
                .length,
          ],
          [
            "Archived",
            diagnostics?.totalArchivedPrograms ??
              programs.filter((item) => item.isDeleted).length,
          ],
          [
            "NY Programs",
            `${diagnostics?.expectedNyActivePublishedCount ?? "-"} / ${diagnostics?.expectedNyProgramCount ?? 36}`,
          ],
          [
            "NY Incentives",
            `${diagnostics?.expectedNyIncentiveCount ?? "-"} / ${diagnostics?.expectedNyProgramCount ?? 36}`,
          ],
          [
            "NY Vectors",
            `${diagnostics?.expectedNyRequirementsVectorCount ?? "-"} / ${diagnostics?.expectedNyProgramCount ?? 36}`,
          ],
        ].map(([label, value]) => (
          <div
            key={label}
            className="rounded border border-gray-200 bg-white px-3 py-2"
          >
            <div className="text-xs text-gray-500">{label}</div>
            <div className="mt-1 text-lg font-semibold text-gray-900">
              {value}
            </div>
          </div>
        ))}
      </div>

      {FeatureFlags.enableIncentiveDbDiagnostics && (
      <div className="rounded border border-gray-200 bg-white p-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div>
            <h3 className="text-sm font-semibold text-black">
              Database Diagnostics
            </h3>
            <div className="mt-1 text-xs text-gray-500">
              {diagnostics?.databaseName || "Database unknown"} ·{" "}
              {diagnostics?.generatedAtUtc
                ? formatAdminDate(diagnostics.generatedAtUtc)
                : "not loaded"}
            </div>
          </div>
          <button
              type="button"
              className="rounded border border-gray-300 px-3 py-1.5 text-xs text-gray-700"
              onClick={() => refreshDiagnostics()}
              disabled={diagnosticsLoading}
            >
            {diagnosticsLoading ? "Checking..." : "Validate Data"}
          </button>
        </div>
        {diagnostics?.issues?.length ? (
          <div className="mt-3 max-h-32 overflow-y-auto rounded bg-gray-50 p-3 text-xs text-gray-700">
            {diagnostics.issues.slice(0, 8).map((issue, index) => (
              <div key={`${issue.code}-${index}`} className="py-1">
                <span
                  className={
                    issue.severity === "error"
                      ? "font-semibold text-red-700"
                      : "font-semibold text-amber-700"
                  }
                >
                  {issue.severity || "info"}
                </span>
                : {issue.message}
              </div>
            ))}
          </div>
        ) : (
          <div className="mt-3 rounded bg-green-50 p-3 text-xs text-green-700">
            No diagnostics issues reported.
          </div>
        )}
      </div>
      )}

      <div className="rounded border border-gray-200 bg-white p-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <h3 className="text-sm font-semibold text-black">
            Incentive Programs
          </h3>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className="rounded bg-zeos-orange-dk px-3 py-2 text-xs text-white"
              onClick={resetProgramForm}
            >
              New Program
            </button>
            <button
              type="button"
              className="rounded border border-gray-300 px-3 py-2 text-xs text-gray-700"
              onClick={exportProgramsCsv}
              disabled={filteredPrograms.length === 0}
            >
              Export CSV
            </button>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-12 gap-3">
          <div className="col-span-12 lg:col-span-4">
            <input
              type="search"
              aria-label="Search incentive programs"
              value={programSearch}
              onChange={(event) => setProgramSearch(event.target.value)}
              placeholder="Search ID, program, incentive, source"
              className="w-full border border-gray-300 rounded px-2 py-2 text-xs"
            />
          </div>
          <div className="col-span-6 lg:col-span-2">
            <select
              aria-label="Filter by source"
              value={programSourceFilter}
              onChange={(event) => setProgramSourceFilter(event.target.value)}
              className="w-full border border-gray-300 rounded px-2 py-2 text-xs"
            >
              <option value="">All sources</option>
              {references?.sources?.map((source) => (
                <option key={source.id} value={source.id}>
                  {source.name}
                </option>
              ))}
            </select>
          </div>
          <div className="col-span-6 lg:col-span-2">
            <select
              aria-label="Filter by category"
              value={programCategoryFilter}
              onChange={(event) => setProgramCategoryFilter(event.target.value)}
              className="w-full border border-gray-300 rounded px-2 py-2 text-xs"
            >
              <option value="">All categories</option>
              {references?.categories?.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>
          <div className="col-span-6 lg:col-span-2">
            <select
              aria-label="Filter by state"
              value={programStateFilter}
              onChange={(event) => setProgramStateFilter(event.target.value)}
              className="w-full border border-gray-300 rounded px-2 py-2 text-xs"
            >
              <option value="">All states</option>
              {stateOptions.map((state) => (
                <option key={state} value={state}>
                  {state}
                </option>
              ))}
            </select>
          </div>
          <div className="col-span-6 lg:col-span-2">
            <select
              aria-label="Filter by status"
              value={programPublishFilter}
              onChange={(event) =>
                setProgramPublishFilter(
                  event.target.value as IncentivePublishFilter,
                )
              }
              className="w-full border border-gray-300 rounded px-2 py-2 text-xs"
            >
              <option value="all">All statuses</option>
              <option value="published">Published</option>
              <option value="draft">Draft</option>
              <option value="archived">Archived</option>
            </select>
          </div>
          <div className="col-span-12 flex flex-wrap gap-4 text-xs text-gray-700">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={includeDrafts}
                onChange={() => setIncludeDrafts((prev) => !prev)}
              />
              Include drafts
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={includeArchived}
                onChange={() => setIncludeArchived((prev) => !prev)}
              />
              Include archived
            </label>
            <span>
              {filteredPrograms.length} shown of {programs.length}
            </span>
          </div>
        </div>

        {programLoading && (
          <p className="mt-4 text-sm text-gray-600">Loading programs...</p>
        )}
        {!programLoading && programs.length === 0 && (
          <p className="mt-4 text-sm text-gray-600">
            No incentive programs found.
          </p>
        )}
        {!programLoading &&
          programs.length > 0 &&
          filteredPrograms.length === 0 && (
            <p className="mt-4 text-sm text-gray-600">
              No incentive programs match these filters.
            </p>
          )}
        {!programLoading && filteredPrograms.length > 0 && (
          <div className="mt-4 overflow-x-auto">
            <table className="w-full min-w-[1100px] text-sm border border-gray-200">
              <thead className="bg-gray-100 text-left text-xs text-gray-600">
                <tr>
                  <th className="px-3 py-2 border-b">Program ID</th>
                  <th className="px-3 py-2 border-b">Incentive ID</th>
                  <th className="px-3 py-2 border-b">Program</th>
                  <th className="px-3 py-2 border-b">Source</th>
                  <th className="px-3 py-2 border-b">Class</th>
                  <th className="px-3 py-2 border-b">State</th>
                  <th className="px-3 py-2 border-b">Status</th>
                  <th className="px-3 py-2 border-b">Updated</th>
                  <th className="px-3 py-2 border-b">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredPrograms.map((program) => (
                  <tr
                    key={program.incentiveProgramId}
                    className={
                      program.isDeleted
                        ? "border-t bg-gray-50 text-gray-500"
                        : "border-t"
                    }
                  >
                    <td className="px-3 py-2 font-mono text-xs">
                      {program.incentiveProgramId}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">
                      {program.incentiveId ?? "-"}
                    </td>
                    <td className="px-3 py-2">
                      <div className="max-w-sm font-medium text-gray-900">
                        {program.name}
                      </div>
                      <div className="max-w-sm truncate text-xs text-gray-500">
                        {program.incentiveName || "-"}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      {referenceName(
                        references?.sources,
                        program.incentiveSourceId,
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      <div>
                        {referenceName(
                          references?.categories,
                          program.incentiveCategoryId,
                        )}
                      </div>
                      <div className="text-gray-500">
                        {referenceName(
                          references?.areas,
                          program.incentiveAreaId,
                        )}{" "}
                        ·{" "}
                        {referenceName(
                          references?.types,
                          program.incentiveTypeId,
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2">{program.stateCode || "-"}</td>
                    <td className="px-3 py-2">
                      {program.isDeleted
                        ? "Archived"
                        : program.isPublished
                          ? "Published"
                          : "Draft"}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {formatAdminDate(program.updatedDate)}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          className="text-xs text-zeos-orange-dk hover:underline"
                          onClick={() => openProgramEditor(program)}
                        >
                          Edit
                        </button>
                        {!program.isDeleted && (
                          <button
                            type="button"
                            className="text-xs text-zeos-orange-dk hover:underline"
                            onClick={() =>
                              setPublishState(program, !program.isPublished)
                            }
                          >
                            {program.isPublished ? "Unpublish" : "Publish"}
                          </button>
                        )}
                        {!program.isDeleted && (
                          <button
                            type="button"
                            className="text-xs text-gray-600 hover:underline"
                            onClick={() => duplicateProgramAsDraft(program)}
                          >
                            Duplicate
                          </button>
                        )}
                        {program.isDeleted ? (
                          <button
                            type="button"
                            className="text-xs text-green-700 hover:underline"
                            onClick={() => restoreProgram(program)}
                          >
                            Restore
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="text-xs text-red-600 hover:underline"
                            onClick={() => archiveProgram(program)}
                          >
                            Archive
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="rounded border border-gray-200 bg-white p-4">
        <h4 className="text-sm font-semibold text-black mb-2">
          Research Assistant
        </h4>
        <div className="grid grid-cols-12 gap-3 text-sm">
          <div className="col-span-12 md:col-span-3">
            <label className="block text-xs text-gray-600 mb-1">Area</label>
            <input
              value={researchForm.area || ""}
              onChange={(event) =>
                setResearchForm({ ...researchForm, area: event.target.value })
              }
              className="w-full border border-gray-300 rounded px-2 py-2"
            />
          </div>
          <div className="col-span-6 md:col-span-2">
            <label className="block text-xs text-gray-600 mb-1">
              Geography Type
            </label>
            <input
              value={researchForm.geographyType || ""}
              onChange={(event) =>
                setResearchForm({
                  ...researchForm,
                  geographyType: event.target.value,
                })
              }
              className="w-full border border-gray-300 rounded px-2 py-2"
            />
          </div>
          <div className="col-span-6 md:col-span-2">
            <label className="block text-xs text-gray-600 mb-1">
              Utility Territory
            </label>
            <input
              value={researchForm.utilityTerritory || ""}
              onChange={(event) =>
                setResearchForm({
                  ...researchForm,
                  utilityTerritory: event.target.value,
                })
              }
              className="w-full border border-gray-300 rounded px-2 py-2"
            />
          </div>
          <div className="col-span-6 md:col-span-2">
            <label className="block text-xs text-gray-600 mb-1">
              Category Focus
            </label>
            <input
              value={researchForm.categoryFocus || ""}
              onChange={(event) =>
                setResearchForm({
                  ...researchForm,
                  categoryFocus: event.target.value,
                })
              }
              className="w-full border border-gray-300 rounded px-2 py-2"
            />
          </div>
          <div className="col-span-6 md:col-span-1">
            <label className="block text-xs text-gray-600 mb-1">Limit</label>
            <input
              type="number"
              value={researchForm.limit ?? 20}
              onChange={(event) =>
                setResearchForm({
                  ...researchForm,
                  limit: event.target.value ? Number(event.target.value) : 20,
                })
              }
              className="w-full border border-gray-300 rounded px-2 py-2"
            />
          </div>
          <div className="col-span-12 md:col-span-2 flex items-end">
            <button
              type="button"
              className="w-full bg-zeos-orange-dk text-white px-4 py-2 rounded text-sm"
              onClick={runResearch}
              disabled={researchLoading}
            >
              {researchLoading ? "Researching..." : "Find"}
            </button>
          </div>
          <div className="col-span-12">
            <label className="block text-xs text-gray-600 mb-1">Notes</label>
            <textarea
              value={researchForm.notes || ""}
              onChange={(event) =>
                setResearchForm({ ...researchForm, notes: event.target.value })
              }
              className="w-full border border-gray-300 rounded px-2 py-2"
              rows={2}
            />
          </div>
        </div>
        {researchResults.length > 0 && (
          <div className="mt-4 grid gap-3 md:grid-cols-2">
            {researchResults.map((result, index) => (
              <div
                key={`${result.programName}-${index}`}
                className="rounded border border-gray-200 p-3 text-xs text-gray-700"
              >
                <div className="font-semibold">
                  {result.programName || "Untitled Program"}
                </div>
                <div className="truncate text-gray-600">
                  {result.programUrl}
                </div>
                <div className="mt-1 line-clamp-3">{result.description}</div>
                <button
                  type="button"
                  className="mt-2 text-xs text-zeos-orange-dk hover:underline"
                  onClick={() => applyResearchResult(result)}
                >
                  Stage as Draft
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {programEditorOpen && renderIncentiveEditor()}
    </div>
  );

  const renderScreeningEngineAdmin = () => (
    <div className="grid grid-cols-12 gap-4 text-sm text-gray-700">
      <div className="col-span-12">
        <h3 className="text-lg font-semibold text-black">Screening Engine</h3>
        <p className="mt-1 text-xs text-gray-600">
          Manage service provider screening prompts, definitions, provider
          profiles, and AI-generated review queue items before they affect live
          runs.
        </p>
      </div>

      <div className="col-span-12 md:col-span-8 border border-gray-200 rounded p-4">
        <div className="flex flex-wrap gap-2 text-xs font-semibold text-gray-600">
          {[
            "Prompts",
            "Report Templates",
            "Program Definitions",
            "Regulation Definitions",
            "Provider Profiles",
            "Review Queue",
          ].map((label) => (
            <span key={label} className="rounded-full bg-gray-100 px-3 py-1">
              {label}
            </span>
          ))}
        </div>

        <div className="mt-4 grid grid-cols-12 gap-3">
          <div className="col-span-12 md:col-span-6">
            <label className="block text-xs text-gray-600 mb-1">Markdown</label>
            <textarea
              className="w-full border border-gray-300 rounded px-2 py-2"
              rows={5}
              placeholder="Prompt or report template markdown"
            />
          </div>
          <div className="col-span-12 md:col-span-6">
            <label className="block text-xs text-gray-600 mb-1">Preview</label>
            <div className="min-h-[132px] rounded border border-dashed border-gray-300 bg-gray-50 p-3 text-xs text-gray-500">
              Preview renders the saved Markdown definition for review before
              Publish.
            </div>
          </div>
          <div className="col-span-12 md:col-span-6">
            <label className="block text-xs text-gray-600 mb-1">
              Structured Form
            </label>
            <input
              className="w-full border border-gray-300 rounded px-2 py-2"
              placeholder="Definition key, geography, required fields"
            />
          </div>
          <div className="col-span-12 md:col-span-6">
            <label className="block text-xs text-gray-600 mb-1">Raw JSON</label>
            <textarea
              className="w-full border border-gray-300 rounded px-2 py-2 font-mono text-xs"
              rows={3}
              placeholder='{"rules":[]}'
            />
          </div>
        </div>

        <div className="mt-4 flex gap-2">
          <button
            type="button"
            className="rounded bg-gray-800 px-4 py-2 text-white"
          >
            Save Draft
          </button>
          <button
            type="button"
            className="rounded bg-zeos-orange-dk px-4 py-2 text-white"
          >
            Publish
          </button>
        </div>
      </div>

      <div className="col-span-12 md:col-span-4 space-y-4">
        <div className="border border-gray-200 rounded p-4">
          <h4 className="font-semibold text-black">Clone Provider</h4>
          <label className="mt-3 block text-xs text-gray-600">
            Source Provider
          </label>
          <input
            className="mt-1 w-full border border-gray-300 rounded px-2 py-2"
            placeholder="BES"
          />
          <label className="mt-3 block text-xs text-gray-600">
            Target Provider
          </label>
          <input
            className="mt-1 w-full border border-gray-300 rounded px-2 py-2"
            placeholder="New provider"
          />
        </div>

        <div className="border border-gray-200 rounded p-4">
          <h4 className="font-semibold text-black">AI Overview</h4>
          <p className="mt-2 text-xs text-gray-600">
            Recommended Action items explain how to cure missing program,
            regulation, and prompt definitions.
          </p>
          <div className="mt-3 rounded bg-gray-50 p-3 text-xs">
            <div className="font-semibold">Recommended Action</div>
            <div className="mt-1 text-gray-600">
              Review suggested incentive mapping.
            </div>
          </div>
          <div className="mt-3 flex gap-2">
            <button
              type="button"
              className="rounded bg-green-600 px-3 py-1.5 text-white"
            >
              Approve
            </button>
            <button
              type="button"
              className="rounded bg-red-600 px-3 py-1.5 text-white"
            >
              Deny
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="w-full bg-white border border-gray-200 rounded p-4">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <button
          type="button"
          className={`text-sm ${activeTab === "users" ? "text-zeos-orange-dk font-semibold" : "text-gray-500"}`}
          onClick={() => setActiveTab("users")}
        >
          Users
        </button>
        <button
          type="button"
          className={`text-sm ${activeTab === "incentives" ? "text-zeos-orange-dk font-semibold" : "text-gray-500"}`}
          onClick={() => setActiveTab("incentives")}
        >
          Incentives
        </button>
        <button
          type="button"
          className={`text-sm ${activeTab === "property-settings" ? "text-zeos-orange-dk font-semibold" : "text-gray-500"}`}
          onClick={() => setActiveTab("property-settings")}
        >
          Property Settings
        </button>
        <button
          type="button"
          className={`text-sm ${activeTab === "customer-settings" ? "text-zeos-orange-dk font-semibold" : "text-gray-500"}`}
          onClick={() => setActiveTab("customer-settings")}
        >
          Customer Settings
        </button>
        <button
          type="button"
          className={`text-sm ${activeTab === "screening-engine" ? "text-zeos-orange-dk font-semibold" : "text-gray-500"}`}
          onClick={() => setActiveTab("screening-engine")}
        >
          Screening Engine
        </button>
        <button
          type="button"
          className={`text-sm ${activeTab === "testing-sandbox" ? "text-zeos-orange-dk font-semibold" : "text-gray-500"}`}
          onClick={() => setActiveTab("testing-sandbox")}
        >
          Testing Sandbox
        </button>
        <button
          type="button"
          className={`text-sm ${activeTab === "metering-attestation" ? "text-zeos-orange-dk font-semibold" : "text-gray-500"}`}
          onClick={() => setActiveTab("metering-attestation")}
        >
          Metering Attestation
        </button>
      </div>

      {/* The incentive editor is an opaque full-screen overlay, so while it is
          open the banner renders inside it; see renderIncentiveEditor. */}
      {!programEditorOpen && renderStatusBanner("mb-4")}

      {activeTab === "users" && renderUserAdmin()}
      {activeTab === "incentives" && renderIncentiveAdmin()}
      {activeTab === "property-settings" && renderPropertySettingsAdmin()}
      {activeTab === "customer-settings" && renderCustomerSettingsAdmin()}
      {activeTab === "screening-engine" && renderScreeningEngineAdmin()}
      {activeTab === "testing-sandbox" && renderTestingSandboxAdmin()}
      {activeTab === "metering-attestation" && (
        <div className="space-y-4">
          <AttachUtilityAuthorization onAttached={() => setUnassignedMetersKey((k) => k + 1)} />
          <MeteringAttestationQueue />
          <UnassignedMeterQueue key={unassignedMetersKey} />
        </div>
      )}
    </div>
  );
};

/**
 * Where a stored matching fact came from.
 *
 * IMPL-002 D4: an agent-inferred fact is usable, but it is stored and shown
 * with its provenance and can never overwrite a confirmed answer. If a
 * reviewer cannot see which facts a screening run inferred, the promise is
 * only half kept — they have no way to spot the one that is wrong.
 *
 * Saving this row confirms the value, which is why editing clears the badge.
 */
const PropertySettingProvenance: React.FC<{
  setting: IAdminPropertySettingItem;
}> = ({ setting }) => {
  if (!setting.evidenceConfidence) {
    return null;
  }

  const inferred = setting.evidenceConfidence === "Inferred";
  const tone = inferred
    ? "border-amber-300 bg-amber-50 text-amber-900"
    : "border-emerald-300 bg-emerald-50 text-emerald-900";

  return (
    <p className="col-span-12 flex flex-wrap items-center gap-2 text-xs text-gray-500">
      <span className={`rounded-full border px-2 py-0.5 ${tone}`}>
        {setting.evidenceConfidence}
      </span>
      {inferred && (
        <span>
          Read from a screening conversation rather than answered. Saving this
          row confirms it.
        </span>
      )}
      {setting.sourceScreeningRunId ? (
        <span className="font-mono">run {setting.sourceScreeningRunId}</span>
      ) : null}
    </p>
  );
};
